import { Injectable } from '@angular/core';
import {NodesThirdPartyDialogComponent} from '../nodes-third-party-dialog/nodes-third-party-dialog.component';
import {NodesDialogComponent} from './nodes-dialog.component';
import {NodeApiService} from '../../../services/api/network/online/node/node-api.service';
import {MatDialog} from '@angular/material/dialog';
import {SnackBarService} from '../../../services/common/snack-bar/snack-bar.service';
import {RoutingInterfaceApiService} from '../../../services/api/network/online/routing-interface/routing-interface-api.service';
import {ConfirmationDialogComponent} from '../../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {LogicalConjunctionService} from '../../../services/common/logical-conjunction/logical-conjunction.service';

@Injectable({
  providedIn: 'root'
})
export class NodesDialogService {

  constructor(public dialog: MatDialog,
              public snackBarService: SnackBarService,
              public routingInterfaceApiService: RoutingInterfaceApiService,
              public logicalConjunctionService: LogicalConjunctionService,
              private nodeApiService: NodeApiService) { }

  addNode(node, onSuccess = () => {}, onFailure = () => {}): void {
    node.isIpAddressProvidedForGUI = true;

    this.nodeApiService.prepareNodeForAdd(node).then(data => {
      const dialogRef = this.dialog.open(NodesDialogComponent, {
        width: '650px',
        data: this.generateDataObject(data, false)
      });

      dialogRef.componentInstance.onApply.subscribe(result => {
        this.nodeApiService.addNode(result.selectedItem).then(() => {
          dialogRef.close();
          this.snackBarService.display(`"${result.selectedItem.name}" successfully added`);
          onSuccess();
        }).catch((error) => {
          console.log(`Exception was thrown in edit node ${result.selectedItem.name}, reason: ${error}`);
        });
      });

      dialogRef.componentInstance.onCancel.subscribe(() => {
        onFailure();
      });
    });
  }

  addThirdPartyNode(node, onSuccess = () => {}, onFailure = () => {}): void {
    const dialogRef = this.dialog.open(NodesThirdPartyDialogComponent, {
      width: '800px',
      minHeight: '400px',
      data: {
        isEditMode: false,
        selectedItem: node
      }
    });

    dialogRef.componentInstance.onApply.subscribe(result => {
      this.nodeApiService.addNode(result.selectedItem).then(() => {
        dialogRef.close();
        this.snackBarService.display(`"${result.selectedItem.name}" successfully added`);
        onSuccess();
      }).catch((error) => {
        console.log(`Exception was thrown in add third party node ${node.name}, reason: ${error}`);
      });
    });

    dialogRef.componentInstance.onCancel.subscribe(() => {
      onFailure();
    });
  }

  edit(node, onSuccess = () => {}, onFailure = () => {}): void {
    if (node.productType === 'THIRD_PARTY') {
      this.editThirdPartyNode(node, onSuccess, onFailure);
    } else {
      this.editNode(node, onSuccess, onFailure);
    }
  }

  delete(node, onSuccess = () => {}): void {
    const confirmationMessage = `Are you sure you want to delete the node: "${node.name}" ?`;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: confirmationMessage
      }
    });

    dialogRef.componentInstance.onApply.subscribe(() => {
      this.nodeApiService.deleteNode(node.id).then(() => {
        dialogRef.close();
        this.snackBarService.display(`${node.name} successfully deleted`);
        onSuccess();
      }).catch((error) => {
        console.log(`Exception was thrown in remove node ${node.name}, reason: ${error}`);
      });
    });
  }

  private editNode(node, onSuccess, onFailure): any {
    this.nodeApiService.prepareNodeForEdit(node.id).then(data => {
      const dialogRef = this.dialog.open(NodesDialogComponent, {
        width: '650px',
        data: this.generateDataObject(data, true)
      });

      dialogRef.componentInstance.onApply.subscribe(result => {
        this.nodeApiService.editNode(result.selectedItem, result.selectedItem.id).then(() => {
          dialogRef.close();
          this.snackBarService.display(`"${result.selectedItem.name}" successfully edited`);
          onSuccess();
        }).catch((error) => {
          console.log(`Exception was thrown in edit node ${node.name}, reason: ${error}`);
        });
      });

      dialogRef.componentInstance.onCancel.subscribe(() => {
        onFailure();
      });
    });
  }

  private editThirdPartyNode(node, onSuccess, onFailure): any {
    Promise.all([this.nodeApiService.getNode(node.id),
      this.routingInterfaceApiService.getRoutingInterfacesForMap({filter: {nodeId: node.id}})]).then(data => {
      data[0].routingInterfaces = data[1].filter(ri => {
        return ri.udpPort > -1;
      });

      const dialogRef = this.dialog.open(NodesThirdPartyDialogComponent, {
        width: '800px',
        minHeight: '400px',
        data: {
          isEditMode: true,
          selectedItem: data[0]
        }
      });

      dialogRef.componentInstance.onApply.subscribe(result => {
        this.nodeApiService.editNode(result.selectedItem, result.selectedItem.id).then(() => {
          dialogRef.close();
          this.snackBarService.display(`"${result.selectedItem.name}" successfully edited`);
          onSuccess();
        }).catch((error) => {
          console.log(`Exception was thrown in edit third party node ${node.name}, reason: ${error}`);
        });
      });

      dialogRef.componentInstance.onCancel.subscribe(() => {
        onFailure();
      });
    });
  }

  private generateDataObject(data, isEditMode): any {
    return {
      isEditMode,
      selectedItem: data.node,
      routingServerGroups: data.routingServerGroups,
      nodeUsers: data.nodeUsers,
      nodeCredentials: data.nodeCredentials,
      isSupportsTeams: data.node && data.node.supportedFeatures?.includes('TEAMS_CONNECTIONS'),
      isLegacyNode: !data.node.supportedFeatures?.includes('ROUTING_SERVER_GROUP')
    };
  }
}
